/**
 * Valuation Wizard Screen
 *
 * Multi-step wizard (4 form steps + 1 result) that collects startup data
 * and calls compute-valuation edge function to estimate a valuation range.
 *
 * Steps:
 *   0 - Company Basics (sector, stage, team size, location)
 *   1 - Financial Info (revenue, growth, business model, toggles)
 *   2 - Market & Competition (market size, intensity, partnerships)
 *   3 - Growth Plans / VC Method (exit timeline, raise, exit value)
 *   4 - Results (animated chart + range + actions)
 */

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  View,
  Text,
  TextInput,
  ScrollView,
  Pressable,
  KeyboardAvoidingView,
  Platform,
  ActivityIndicator,
  Switch,
} from 'react-native';
import { useRouter } from 'expo-router';
import { LinearGradient } from 'expo-linear-gradient';
import * as Haptics from 'expo-haptics';
import Animated, {
  FadeIn,
  FadeInDown,
  FadeInUp,
  } from 'react-native-reanimated';
import {
  ChevronLeft,
  CheckCircle2,
  Calculator,
  Building2,
  DollarSign,
  Target,
  Rocket,
  RefreshCw,
  ArrowRight,
  Info,
  Sparkles,
} from 'lucide-react-native';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useThemeColors } from '@/lib/useThemeColors';
import { useProfileStore } from '@/lib/state/profile-store';
import { cn } from '@/lib/cn';
import { handleError } from '@/lib/errors';
import { callEdgeFunction } from '@/lib/supabase/edge-functions';
import { mode } from '@/lib/config/app-mode';
import { useMyStartup } from '@/lib/startup/use-my-startup';
import { AppLayout } from '@/components/AppLayout';
import { ScreenFrame } from '@/components/ScreenFrame';
import { AppModal, ModalAction } from '@/components/modal';
import AnimatedPressable from '@/components/AnimatedPressable';
import { prefillFromProfile, countPrefilled } from '@/lib/valuation/prefill';
import type {
  ValuationInput,
  ValuationResult,
  ValuationMethodBar,
  WizardStep,
  ValuationStage,
  BusinessModel,
  MarketSize,
  CompetitiveIntensity,
} from '@/lib/valuation/types';
import {
  WIZARD_STEPS,
  SECTORS,
  REGIONS,
  STAGE_OPTIONS,
  BUSINESS_MODEL_OPTIONS,
  MARKET_SIZE_OPTIONS,
  COMPETITIVE_INTENSITY_OPTIONS,
} from '@/lib/valuation/types';

// ============================================================================
// Helpers
// ============================================================================

/** Format a number with Swiss apostrophe grouping (1'500'000) */
function formatCHF(value: number): string {
  if (value === 0) return '0';
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, "'");
}

/** Parse a CHF-formatted string back to number */
function parseCHF(text: string): number {
  const cleaned = text.replace(/[^0-9]/g, '');
  return cleaned ? parseInt(cleaned, 10) : 0;
}

/** Format a large number for display (1.5M, 2.3M, etc.) */
function formatLargeValue(value: number): string {
  if (value >= 1_000_000_000) {
    return `CHF ${(value / 1_000_000_000).toFixed(1)}B`;
  }
  if (value >= 1_000_000) {
    return `CHF ${(value / 1_000_000).toFixed(1)}M`;
  }
  if (value >= 1_000) {
    return `CHF ${(value / 1_000).toFixed(0)}K`;
  }
  return `CHF ${value}`;
}

// ============================================================================
// Mock Data
// ============================================================================

function getMockValuationResult(): ValuationResult {
  return {
    id: `val_mock_${Date.now()}`,
    startup_id: 'mock_startup_id',
    config_version: '1.0.0',
    methodology_version: '1.0.0',
    input_hash: 'mock_hash',
    wizard_inputs: {} as ValuationInput,
    methods: {
      berkus: {
        value: 1_400_000,
        confidence: 70,
        breakdown: { soundIdea: 300000, prototype: 300000, qualityTeam: 400000, strategicRelations: 200000, productRollout: 200000 },
        applicable: true,
        currency: 'CHF',
      },
      scorecard: {
        value: 2_000_000,
        confidence: 65,
        breakdown: { teamStrength: 1.2, marketSize: 1.0, technology: 0.9, competition: 0.8, marketing: 1.1 },
        applicable: true,
        currency: 'CHF',
      },
      risk_factor_sum: {
        value: 1_300_000,
        confidence: 75,
        breakdown: { management: -1, stage: -2, legislation: 0, manufacturing: 0, sales: -1, funding: 1, competition: -1, technology: 0, litigation: 0, international: 0, reputation: 0, exit: -1 },
        applicable: true,
        currency: 'CHF',
      },
      vc_method: {
        value: 2_500_000,
        confidence: 55,
        breakdown: { exitValue: 20000000, exitMultiple: 8, roi: 10, requiredOwnership: 0.25 },
        applicable: true,
        currency: 'CHF',
      },
    },
    suggested_range: {
      min: 1_300_000,
      max: 2_500_000,
      median: 1_700_000,
      iqr_low: 1_350_000,
      iqr_high: 2_250_000,
      currency: 'CHF',
    },
    applicable_methods: 4,
    created_at: new Date().toISOString(),
    cached: false,
    persisted: false,
  };
}

// ============================================================================
// Method Colors
// ============================================================================

const METHOD_COLORS: Record<string, string> = {
  berkus: '#6366F1',      // Indigo
  scorecard: '#F59E0B',   // Amber
  risk_factor_sum: '#10B981', // Emerald
  vc_method: '#EC4899',   // Pink
};

const METHOD_LABELS: Record<string, string> = {
  berkus: 'Berkus',
  scorecard: 'Scorecard',
  risk_factor_sum: 'Risk Factor',
  vc_method: 'VC Method',
};

// ============================================================================
// Main Screen Component
// ============================================================================

export default function ValuationWizardScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const startupProfile = useProfileStore((s) => s.startupProfile);
  const queryClient = useQueryClient();
  // valuation_results is keyed by startup_profiles.id (B3 fix) — the cache
  // seed below must use the same key ValuationCard reads.
  const { startupId } = useMyStartup();

  // ---------------------------------------------------------------------------
  // Pre-fill from profile
  // ---------------------------------------------------------------------------
  const prefilled = useMemo(
    () => prefillFromProfile(startupProfile),
    [startupProfile],
  );
  const prefilledStats = useMemo(() => countPrefilled(prefilled), [prefilled]);

  // ---------------------------------------------------------------------------
  // Form State
  // ---------------------------------------------------------------------------
  const [currentStep, setCurrentStep] = useState<WizardStep>(0);
  const [formData, setFormData] = useState<Partial<ValuationInput>>({
    sector: prefilled.sector ?? '',
    stage: prefilled.stage ?? 'seed',
    region: prefilled.region ?? '',
    teamSize: prefilled.teamSize ?? 0,
    monthlyRevenue: prefilled.monthlyRevenue ?? 0,
    revenueGrowthRate: prefilled.revenueGrowthRate ?? 0,
    businessModel: prefilled.businessModel ?? 'saas',
    marketSize: 'medium' as MarketSize,
    competitiveIntensity: 'medium' as CompetitiveIntensity,
    hasPrototype: true,
    hasRevenue: prefilled.hasRevenue ?? false,
    hasStrategicPartnerships: false,
    exitTimeline: 7,
    targetRaise: prefilled.targetRaise ?? 0,
    expectedExitValue: 0,
  });
  const [stepErrors, setStepErrors] = useState<Record<string, string>>({});
  const [result, setResult] = useState<ValuationResult | null>(null);
  const [confirmExit, setConfirmExit] = useState(false);

  /**
   * WHY AN EXPLICIT RESET AND A GENERATION COUNTER BOTH EXIST.
   *
   * This screen is a `Tabs.Screen` (`(startup)/_layout.tsx`), and a bottom-tab
   * screen is NOT unmounted when you navigate away from it. React Navigation 7
   * keeps an append-only `loaded` list — `@react-navigation/bottom-tabs`
   * `BottomTabView.tsx:92-97`, with nothing anywhere that removes a key — so
   * once the wizard has been opened it stays mounted for the life of the tab
   * navigator. `detachInactiveScreens` detaches only the NATIVE view, and
   * `unmountOnBlur` no longer exists in v7.
   *
   * So "Discard and leave" navigating away discarded nothing: every answer, the
   * step index and the result were still in memory, and re-entering showed the
   * same half-filled form the dialog had just promised to throw away. The reset
   * must be explicit, and it must live in the DISCARD handler rather than in a
   * blur listener — a blur listener fires on every ordinary tab switch and would
   * wipe the form of anyone who glanced at Messages mid-wizard.
   *
   * `runIdRef` is bumped by that reset. `inFlightRunIdRef` records which
   * generation a request belongs to, so a calculation that resolves AFTER a
   * discard is ignored instead of resurrecting the discarded answers.
   */
  const runIdRef = useRef(0);
  const inFlightRunIdRef = useRef(0);

  /**
   * DRAFT SEMANTICS, STATED RATHER THAN INVENTED.
   *
   * Nothing in this wizard is persisted. `formData` is component state seeded
   * once from the profile prefill; there is no AsyncStorage write, no store
   * slice, no server draft. The only thing that ever leaves this screen is the
   * RESULT, seeded into the query cache on success. So leaving genuinely
   * discards the answers, and the honest thing is to say so before it happens —
   * not to invent a backend draft system, and not to drop the data silently.
   *
   * The snapshot is captured once at mount so "dirty" means "differs from what
   * we pre-filled", not "differs from empty" — otherwise every profile-prefilled
   * wizard would claim unsaved work the moment it opened.
   */
  const initialFormData = useRef(formData).current;
  const isDirty = useMemo(
    () => JSON.stringify(formData) !== JSON.stringify(initialFormData),
    [formData, initialFormData],
  );

  // Dropdown visibility state
  const [showSectorPicker, setShowSectorPicker] = useState(false);
  const [showRegionPicker, setShowRegionPicker] = useState(false);

  // ---------------------------------------------------------------------------
  // Field updater
  // ---------------------------------------------------------------------------
  const updateField = useCallback(
    <K extends keyof ValuationInput>(key: K, value: ValuationInput[K]) => {
      setFormData((prev) => ({ ...prev, [key]: value }));
      if (stepErrors[key]) {
        setStepErrors((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
      }
    },
    [stepErrors],
  );

  // ---------------------------------------------------------------------------
  // Validation
  // ---------------------------------------------------------------------------
  const validateStep = useCallback(
    (step: WizardStep): boolean => {
      const errors: Record<string, string> = {};

      switch (step) {
        case 0:
          if (!formData.sector) errors.sector = 'Sector is required';
          if (!formData.region) errors.region = 'Region is required';
          if (!formData.teamSize || formData.teamSize <= 0) errors.teamSize = 'Team size must be greater than 0';
          break;
        case 1:
          if (formData.hasRevenue && (!formData.monthlyRevenue || formData.monthlyRevenue <= 0)) {
            errors.monthlyRevenue = 'Please enter your monthly revenue';
          }
          break;
        case 2:
          // Market & competition — all fields have defaults, no hard requirements
          break;
        case 3:
          if (!formData.exitTimeline || formData.exitTimeline < 1) {
            errors.exitTimeline = 'Exit timeline must be at least 1 year';
          }
          break;
      }

      setStepErrors(errors);
      return Object.keys(errors).length === 0;
    },
    [formData],
  );

  // ---------------------------------------------------------------------------
  // API Mutation
  // ---------------------------------------------------------------------------
  const { mutate: computeMutate, isPending: computeIsPending } = useMutation({
    mutationFn: async (input: Partial<ValuationInput>) => {
      inFlightRunIdRef.current = runIdRef.current;
      if (mode.isMock) {
        // Simulate API delay in mock mode
        await new Promise((resolve) => setTimeout(resolve, 1500));
        return getMockValuationResult();
      }

      const response = await callEdgeFunction<ValuationResult>(
        'compute-valuation',
        input,
        { timeout: 60_000 },
      );

      if (response.error || !response.data) {
        throw new Error(response.error ?? 'Failed to compute valuation');
      }

      return response.data;
    },
    onSuccess: (data) => {
      // A DISCARD THAT HAPPENED WHILE THIS WAS IN FLIGHT WINS. The request
      // cannot be cancelled (the only AbortController is bound to the 60s
      // timeout), so a calculation started before "Discard and leave" still
      // resolves afterwards — and without this guard it would drive the screen
      // we had just reset to the results step, resurrecting the answers the
      // dialog said were gone. The server-side write is unaffected, and
      // ValuationCard fetches the figure on its own.
      if (inFlightRunIdRef.current !== runIdRef.current) return;
      setResult(data);
      setCurrentStep(4);
      // Populate the React Query cache so ValuationCard picks up the result immediately
      queryClient.setQueryData(['valuation', 'latest', startupId], data);
      if (Platform.OS !== 'web') {
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
      }
    },
    onError: (error) => {
      // Same reasoning as onSuccess: a failure belonging to a discarded run
      // must not raise an error banner on a freshly reset form.
      if (inFlightRunIdRef.current !== runIdRef.current) return;
      const { message, isCancelled } = handleError(error, {
        context: 'ValuationWizard.compute',
      });
      if (!isCancelled) {
        setStepErrors({ _api: message });
      }
    },
  });

  // ---------------------------------------------------------------------------
  // Navigation
  // ---------------------------------------------------------------------------
  const handleNext = useCallback(() => {
    if (!validateStep(currentStep)) return;

    if (currentStep === 3) {
      // Submit on last form step
      computeMutate(formData);
    } else {
      setCurrentStep((prev) => (prev + 1) as WizardStep);
      if (Platform.OS !== 'web') {
        Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
      }
    }
  }, [currentStep, formData, validateStep, computeMutate]);

  /**
   * STEP navigation only. It used to do both jobs — walk steps AND leave the
   * feature at step zero — which is why the wizard read as onboarding: the one
   * prominent control meant two different things depending on invisible state,
   * and at step zero it called `router.back()` with no `canGoBack()` guard, so
   * a deep link left a dead arrow. Leaving is now `handleExit`, and it lives in
   * the persistent page header where a way out belongs.
   */
  const handlePreviousStep = useCallback(() => {
    setCurrentStep((prev) => (prev > 0 ? prev - 1 : prev) as WizardStep);
  }, []);

  /**
   * Leaving the feature. Returns false to ScreenFrame when it needs to ask
   * first, which holds the navigation until the user answers.
   *
   * Exiting mid-calculation is deliberately allowed. The request cannot be
   * cancelled (the only AbortController is bound to the 60s timeout) and the
   * edge function persists server-side regardless, so the figure is not lost —
   * it simply is not applied to a screen that no longer exists, and
   * ValuationCard re-fetches it. Navigating away triggers no second calculation.
   */
  const handleExit = useCallback(() => {
    if (isDirty && !result) {
      setConfirmExit(true);
      return false;
    }
    return true;
  }, [isDirty, result]);

  /**
   * What "Discard" has to actually do, given that leaving does not unmount us.
   * Bumping `runIdRef` first is what makes any in-flight calculation ignore its
   * own result — see the block where the refs are declared.
   */
  const resetWizard = useCallback(() => {
    runIdRef.current += 1;
    setCurrentStep(0);
    setFormData(initialFormData);
    setStepErrors({});
    setResult(null);
  }, [initialFormData]);

  const handleRecalculate = useCallback(() => {
    setResult(null);
    setCurrentStep(0);
    setStepErrors({});
  }, []);

  // ---------------------------------------------------------------------------
  // Convert results for chart
  // ---------------------------------------------------------------------------
  const methodBars: ValuationMethodBar[] = useMemo(() => {
    if (!result) return [];
    return Object.entries(result.methods)
      .filter(([, m]) => m.applicable)
      .map(([key, m]) => ({
        method: key,
        label: METHOD_LABELS[key] ?? key,
        value: m.value,
        confidence: m.confidence,
        color: METHOD_COLORS[key] ?? colors.primary[500],
      }))
      .sort((a, b) => a.value - b.value);
  }, [result, colors]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  const isFormStep = currentStep < 4;
  const FORM_STEP_COUNT = 4;
  const stepIcons = [Building2, DollarSign, Target, Rocket];
  const StepIcon = stepIcons[currentStep] ?? Calculator;

  const content = (
    <View style={{ flex: 1, backgroundColor: colors.surface.secondary }}>
      {/* The title and the way out are PERSISTENT — form steps, calculating,
         API error and results alike. They used to live inside the `isFormStep`
         block, so the results screen rendered with no header and no exit at
         all, and the only way off it was a "Back to Dashboard" button below a
         long report. ScreenFrame also supplies the top inset that the web
         branch's empty `edges` left unowned in a standalone PWA. */}
      <ScreenFrame
        title="Valuation"
        eyebrow={isFormStep ? `Step ${currentStep + 1} of ${FORM_STEP_COUNT}` : 'Result'}
        back={{ fallback: '/(startup)/score', label: 'Score', onPress: handleExit }}
      >
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={{ flex: 1 }}
        >
          {/* ---- Header ---- */}
          {isFormStep && (
            <Animated.View
              entering={FadeInDown.duration(400)}
              style={{ paddingHorizontal: 20, paddingTop: 16, paddingBottom: 8 }}
            >
              {/* Progress only. The arrow that used to sit here did double duty
                 as "previous step" and "leave the wizard"; Previous is now a
                 labelled control in the step region below and Exit is in the
                 page header above, which stays put through every state. */}
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                <View style={{ flex: 1, flexDirection: 'row', gap: 6 }}>
                  {Array.from({ length: FORM_STEP_COUNT }).map((_, i) => (
                    <View
                      key={i}
                      style={{
                        flex: 1,
                        height: 4,
                        borderRadius: 2,
                        backgroundColor:
                          i <= currentStep ? colors.primary[500] : colors.border.light,
                      }}
                    />
                  ))}
                </View>
              </View>

              {/* Step title */}
              <View style={{ marginTop: 20 }}>
                {/* The step count is the frame's eyebrow now. Printing it here
                   too showed "STEP 1 OF 4" twice on the same screen, a few
                   points apart, once the persistent header arrived. */}
                <View style={{ marginBottom: 4 }}>
                  <StepIcon size={20} color={colors.primary[600]} />
                </View>
                <Text
                  className="text-2xl font-bold"
                  style={{
                    color: colors.text.primary,
                    letterSpacing: -0.5,
                  }}
                >
                  {WIZARD_STEPS[currentStep].title}
                </Text>
                <Text
                  className="text-sm"
                  style={{
                    color: colors.text.secondary,
                    marginTop: 4,
                  }}
                >
                  {WIZARD_STEPS[currentStep].subtitle}
                </Text>
              </View>

              {/* Pre-fill banner (step 0 only) */}
              {currentStep === 0 && prefilledStats.count > 0 && (
                <Animated.View entering={FadeIn.delay(300).duration(400)}>
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 8,
                      marginTop: 12,
                      paddingHorizontal: 14,
                      paddingVertical: 10,
                      borderRadius: 12,
                      backgroundColor: colors.primary[50],
                    }}
                  >
                    <Sparkles size={16} color={colors.primary[600]} />
                    <Text
                      className="text-sm font-medium"
                      style={{
                        flex: 1,
                        color: colors.primary[700],
                      }}
                    >
                      {prefilledStats.count} of {prefilledStats.total} fields pre-filled from your profile
                    </Text>
                  </View>
                </Animated.View>
              )}
            </Animated.View>
          )}

          {/* ---- Form Content ---- */}
          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={{ paddingHorizontal: 20, paddingBottom: 40 }}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            {currentStep === 0 && (
              <StepCompanyBasics
                formData={formData}
                updateField={updateField}
                errors={stepErrors}
                prefilled={prefilled}
                showSectorPicker={showSectorPicker}
                setShowSectorPicker={setShowSectorPicker}
                showRegionPicker={showRegionPicker}
                setShowRegionPicker={setShowRegionPicker}
                colors={colors}
              />
            )}
            {currentStep === 1 && (
              <StepFinancialInfo
                formData={formData}
                updateField={updateField}
                errors={stepErrors}
                prefilled={prefilled}
                colors={colors}
              />
            )}
            {currentStep === 2 && (
              <StepMarketCompetition
                formData={formData}
                updateField={updateField}
                colors={colors}
              />
            )}
            {currentStep === 3 && (
              <StepGrowthPlans
                formData={formData}
                updateField={updateField}
                errors={stepErrors}
                colors={colors}
              />
            )}
            {currentStep === 4 && result && (
              <StepResults
                result={result}
                methodBars={methodBars}
                onRecalculate={handleRecalculate}
                onBackToDashboard={() => router.push('/(startup)/startup-dashboard')}
                colors={colors}
              />
            )}

            {/* API error display */}
            {stepErrors._api && (
              <Animated.View entering={FadeIn.duration(300)}>
                <View
                  style={{
                    marginTop: 16,
                    padding: 14,
                    borderRadius: 12,
                    backgroundColor: colors.red.light,
                  }}
                >
                  <Text
                    className="text-sm font-medium"
                    style={{
                      color: colors.error,
                    }}
                  >
                    {stepErrors._api}
                  </Text>
                </View>
              </Animated.View>
            )}
          </ScrollView>

          {/* ---- Bottom Navigation ---- */}
          {isFormStep && (
            <View
              style={{
                paddingHorizontal: 20,
                paddingBottom: Platform.OS === 'ios' ? 8 : 16,
                paddingTop: 12,
                borderTopWidth: 1,
                borderTopColor: colors.border.light,
                backgroundColor: colors.surface.primary,
              }}
            >
              {currentStep === 3 ? (
                // THE FINAL FORM STEP KEEPS PREVIOUS. It used to render Calculate
                // alone, and the header arrow that had been the only other way
                // back was removed when Exit took its place — so step four was a
                // one-way door: you could see every answer you had given and
                // could not reach any of them to correct it.
                //
                // Previous is disabled while a calculation is in flight. Letting
                // someone edit step three underneath a request that is already
                // using those numbers would show a result that does not match the
                // form it came from.
                <View style={{ flexDirection: 'row', gap: 12 }}>
                  <View style={{ width: 120, height: 56, borderRadius: 16, overflow: 'hidden', backgroundColor: colors.surface.secondary, borderWidth: 1, borderColor: colors.border.medium, opacity: computeIsPending ? 0.5 : 1 }}>
                    <AnimatedPressable
                      onPress={handlePreviousStep}
                      disabled={computeIsPending}
                      accessibilityRole="button"
                      accessibilityLabel="Previous step"
                      accessibilityState={{ disabled: computeIsPending }}
                      style={{ flex: 1, alignItems: 'center', justifyContent: 'center', flexDirection: 'row', gap: 6 }}
                    >
                      <ChevronLeft size={18} color={colors.text.primary} />
                      <Text className="text-base font-semibold" style={{ color: colors.text.primary }}>
                        Previous
                      </Text>
                    </AnimatedPressable>
                  </View>
                <AnimatedPressable
                  onPress={handleNext}
                  disabled={computeIsPending}
                  accessibilityRole="button"
                  accessibilityLabel="Calculate valuation"
                  accessibilityState={{ disabled: computeIsPending, busy: computeIsPending }}
                  style={{ flex: 1 }}
                >
                  <LinearGradient
                    colors={[colors.primary[500], colors.primary[700]]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 0 }}
                    style={{
                      height: 56,
                      borderRadius: 16,
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'row',
                      gap: 10,
                      opacity: computeIsPending ? 0.7 : 1,
                    }}
                  >
                    {computeIsPending ? (
                      <>
                        <ActivityIndicator size="small" color="#FFFFFF" />
                        <Text
                          className="text-base font-semibold"
                          style={{ color: '#FFFFFF' }}
                        >
                          Calculating...
                        </Text>
                      </>
                    ) : (
                      <>
                        <Calculator size={20} color="#FFFFFF" />
                        <Text
                          className="text-base font-semibold"
                          style={{ color: '#FFFFFF' }}
                        >
                          Calculate Valuation
                        </Text>
                      </>
                    )}
                  </LinearGradient>
                </AnimatedPressable>
                </View>
              ) : (
                // Step controls. Previous is a peer of Continue and reads as a
                // step control, which is exactly what it is — leaving the
                // wizard is the Exit action in the header, not this.
                <View style={{ flexDirection: 'row', gap: 12 }}>
                  {currentStep > 0 && (
                    <View style={{ width: 120, height: 52, borderRadius: 14, overflow: 'hidden', backgroundColor: colors.surface.secondary, borderWidth: 1, borderColor: colors.border.medium }}>
                      <AnimatedPressable
                        onPress={handlePreviousStep}
                        accessibilityRole="button"
                        accessibilityLabel="Previous step"
                        style={{
                          flex: 1,
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexDirection: 'row',
                          gap: 6,
                        }}
                      >
                        <ChevronLeft size={18} color={colors.text.primary} />
                        <Text className="text-base font-semibold" style={{ color: colors.text.primary }}>
                          Previous
                        </Text>
                      </AnimatedPressable>
                    </View>
                  )}
                  <View style={{ flex: 1, height: 52, borderRadius: 14, overflow: 'hidden', backgroundColor: colors.primary[600] }}>
                    <AnimatedPressable
                      onPress={handleNext}
                      accessibilityRole="button"
                      accessibilityLabel="Continue to the next step"
                      style={{
                        flex: 1,
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'row',
                        gap: 8,
                      }}
                    >
                      <Text
                        className="text-base font-semibold"
                        style={{ color: '#FFFFFF' }}
                      >
                        Continue
                      </Text>
                      <ArrowRight size={18} color="#FFFFFF" />
                    </AnimatedPressable>
                  </View>
                </View>
              )}
            </View>
          )}
        </KeyboardAvoidingView>
      </ScreenFrame>

      {/* Exiting discards the answers, because nothing here is persisted. Say
         so, name the consequence, and let it be cancelled. Centered card per the
         modal rules — never a bottom sheet, which would cover the tab bar. */}
      <AppModal
        visible={confirmExit}
        onClose={() => setConfirmExit(false)}
        title="Leave the valuation?"
        footer={
          <AppModal.Actions>
            <ModalAction
              label="Keep editing"
              variant="neutral"
              onPress={() => setConfirmExit(false)}
            />
            <ModalAction
              label="Discard and leave"
              variant="destructive"
              onPress={() => {
                setConfirmExit(false);
                // Reset BEFORE navigating. The screen stays mounted either way,
                // so this is the only thing that makes the dialog's promise true.
                resetWizard();
                if (router.canGoBack()) router.back();
                else router.replace('/(startup)/score');
              }}
            />
          </AppModal.Actions>
        }
      >
        <Text className="text-base" style={{ color: colors.text.secondary }}>
          Your answers are not saved anywhere yet. If you leave now, they are
          cleared and this valuation starts again from the first step.
        </Text>
      </AppModal>
    </View>
  );

  return (
    <AppLayout title="Valuation Wizard">
      {content}
    </AppLayout>
  );
}

// ============================================================================
// Shared Field Components
// ============================================================================

interface FieldLabelProps {
  label: string;
  required?: boolean;
  prefilled?: boolean;
  colors: ReturnType<typeof useThemeColors>;
}

function FieldLabel({ label, required, prefilled, colors }: FieldLabelProps) {
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 8 }}>
      <Text
        className="text-sm font-medium"
        style={{ color: colors.text.secondary }}
      >
        {label}
        {required && <Text style={{ color: colors.error }}> *</Text>}
      </Text>
      {prefilled && (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
          <View
            style={{
              width: 6,
              height: 6,
              borderRadius: 3,
              backgroundColor: colors.primary[500],
            }}
          />
          <Text
            className="text-xs"
            style={{ color: colors.primary[600] }}
          >
            From profile
          </Text>
        </View>
      )}
    </View>
  );
}

interface FieldErrorProps {
  error?: string;
  colors: ReturnType<typeof useThemeColors>;
}

function FieldError({ error, colors }: FieldErrorProps) {
  if (!error) return null;
  return (
    <Text
      className="text-xs"
      style={{
        color: colors.error,
        marginTop: 4,
      }}
    >
      {error}
    </Text>
  );
}

// ============================================================================
// Segmented Control
// ============================================================================

interface SegmentedControlProps<T extends string> {
  options: { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
  colors: ReturnType<typeof useThemeColors>;
}

function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  colors,
}: SegmentedControlProps<T>) {
  return (
    <View
      style={{
        flexDirection: 'row',
        borderRadius: 12,
        backgroundColor: colors.surface.tertiary,
        padding: 3,
      }}
    >
      {options.map((opt) => {
        const isActive = opt.value === value;
        return (
          <Pressable
            key={opt.value}
            onPress={() => onChange(opt.value)}
            style={{
              flex: 1,
              paddingVertical: 10,
              borderRadius: 10,
              alignItems: 'center',
              backgroundColor: isActive ? colors.surface.primary : 'transparent',
              ...(isActive
                ? {
                    shadowColor: '#000',
                    shadowOpacity: 0.05,
                    shadowRadius: 10,
                    shadowOffset: { width: 0, height: 2 },
                    elevation: 2,
                  }
                : {}),
            }}
          >
            <Text
              className={cn('text-sm', isActive ? 'font-semibold' : 'font-medium')}
              style={{ color: isActive ? colors.primary[600] : colors.text.secondary }}
            >
              {opt.label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

// ============================================================================
// Dropdown Picker
// ============================================================================

interface DropdownPickerProps {
  options: readonly string[];
  value: string;
  placeholder: string;
  visible: boolean;
  onToggle: (v: boolean) => void;
  onChange: (v: string) => void;
  error?: string;
  colors: ReturnType<typeof useThemeColors>;
}

function DropdownPicker({
  options,
  value,
  placeholder,
  visible,
  onToggle,
  onChange,
  error,
  colors,
}: DropdownPickerProps) {
  return (
    <View>
      <Pressable
        onPress={() => onToggle(!visible)}
        style={{
          backgroundColor: colors.surface.primary,
          borderWidth: 1,
          borderColor: error ? colors.error : colors.border.light,
          borderRadius: 14,
          height: 48,
          paddingHorizontal: 16,
          justifyContent: 'center',
        }}
      >
        <Text
          className="text-base"
          style={{ color: value ? colors.text.primary : colors.text.tertiary }}
        >
          {value || placeholder}
        </Text>
      </Pressable>

      {visible && (
        <View
          style={{
            backgroundColor: colors.surface.primary,
            borderRadius: 14,
            marginTop: 6,
            borderWidth: 1,
            borderColor: colors.border.light,
            maxHeight: 240,
          }}
        >
          <ScrollView nestedScrollEnabled showsVerticalScrollIndicator={false}>
            {options.map((opt, idx) => (
              <Pressable
                key={opt}
                onPress={() => {
                  onChange(opt);
                  onToggle(false);
                }}
                style={{
                  paddingHorizontal: 16,
                  paddingVertical: 12,
                  borderBottomWidth: idx < options.length - 1 ? 1 : 0,
                  borderBottomColor: colors.border.light,
                  backgroundColor: value === opt ? colors.primary[50] : 'transparent',
                }}
              >
                <Text
                  className="text-sm"
                  style={{ color: value === opt ? colors.primary[600] : colors.text.primary }}
                >
                  {opt}
                </Text>
              </Pressable>
            ))}
          </ScrollView>
        </View>
      )}
    </View>
  );
}

// ============================================================================
// CHF Input
// ============================================================================

interface CHFInputProps {
  value: number;
  onChange: (v: number) => void;
  placeholder?: string;
  error?: string;
  colors: ReturnType<typeof useThemeColors>;
}

function CHFInput({ value, onChange, placeholder, error, colors }: CHFInputProps) {
  const [displayValue, setDisplayValue] = useState(value > 0 ? formatCHF(value) : '');

  useEffect(() => {
    if (value > 0 && displayValue === '') {
      setDisplayValue(formatCHF(value));
    }
  }, [value]);

  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: colors.surface.primary,
        borderWidth: 1,
        borderColor: error ? colors.error : colors.border.light,
        borderRadius: 14,
        height: 48,
        overflow: 'hidden',
      }}
    >
      <View
        style={{
          paddingHorizontal: 14,
          height: '100%',
          justifyContent: 'center',
          backgroundColor: colors.surface.tertiary,
          borderRightWidth: 1,
          borderRightColor: colors.border.light,
        }}
      >
        <Text
          className="text-sm font-semibold"
          style={{ color: colors.text.secondary }}
        >
          CHF
        </Text>
      </View>
      <TextInput
        className="text-base"
        style={{
          flex: 1,
          height: '100%',
          paddingHorizontal: 14,
          color: colors.text.primary,
        }}
        placeholder={placeholder ?? "0"}
        placeholderTextColor={colors.text.tertiary}
        keyboardType="number-pad"
        value={displayValue}
        onChangeText={(text) => {
          const num = parseCHF(text);
          setDisplayValue(num > 0 ? formatCHF(num) : text.replace(/[^0-9]/g, ''));
          onChange(num);
        }}
      />
    </View>
  );
}

// ============================================================================
// Step 0: Company Basics
// ============================================================================

interface StepCompanyBasicsProps {
  formData: Partial<ValuationInput>;
  updateField: <K extends keyof ValuationInput>(key: K, value: ValuationInput[K]) => void;
  errors: Record<string, string>;
  prefilled: ReturnType<typeof prefillFromProfile>;
  showSectorPicker: boolean;
  setShowSectorPicker: (v: boolean) => void;
  showRegionPicker: boolean;
  setShowRegionPicker: (v: boolean) => void;
  colors: ReturnType<typeof useThemeColors>;
}

function StepCompanyBasics({
  formData,
  updateField,
  errors,
  prefilled,
  showSectorPicker,
  setShowSectorPicker,
  showRegionPicker,
  setShowRegionPicker,
  colors,
}: StepCompanyBasicsProps) {
  return (
    <Animated.View entering={FadeInUp.delay(200).duration(400)} style={{ gap: 20, marginTop: 16 }}>
      {/* Sector */}
      <View>
        <FieldLabel label="Sector" required prefilled={!!prefilled.sector} colors={colors} />
        <DropdownPicker
          options={SECTORS}
          value={formData.sector ?? ''}
          placeholder="Select sector"
          visible={showSectorPicker}
          onToggle={setShowSectorPicker}
          onChange={(v) => updateField('sector', v)}
          error={errors.sector}
          colors={colors}
        />
        <FieldError error={errors.sector} colors={colors} />
      </View>

      {/* Stage */}
      <View>
        <FieldLabel label="Stage" required prefilled={!!prefilled.stage} colors={colors} />
        <SegmentedControl
          options={STAGE_OPTIONS}
          value={formData.stage ?? 'seed'}
          onChange={(v) => updateField('stage', v as ValuationStage)}
          colors={colors}
        />
      </View>

      {/* Team Size */}
      <View>
        <FieldLabel label="Team Size" required prefilled={!!prefilled.teamSize} colors={colors} />
        <TextInput
          className="text-base"
          style={{
            backgroundColor: colors.surface.primary,
            borderWidth: 1,
            borderColor: errors.teamSize ? colors.error : colors.border.light,
            borderRadius: 14,
            height: 48,
            paddingHorizontal: 16,
            color: colors.text.primary,
          }}
          placeholder="e.g., 8"
          placeholderTextColor={colors.text.tertiary}
          keyboardType="number-pad"
          value={formData.teamSize ? String(formData.teamSize) : ''}
          onChangeText={(text) => {
            const num = parseInt(text.replace(/[^0-9]/g, ''), 10);
            updateField('teamSize', isNaN(num) ? 0 : num);
          }}
        />
        <FieldError error={errors.teamSize} colors={colors} />
      </View>

      {/* Region */}
      <View>
        <FieldLabel label="Region" required prefilled={!!prefilled.region} colors={colors} />
        <DropdownPicker
          options={REGIONS}
          value={formData.region ?? ''}
          placeholder="Select region"
          visible={showRegionPicker}
          onToggle={setShowRegionPicker}
          onChange={(v) => updateField('region', v)}
          error={errors.region}
          colors={colors}
        />
        <FieldError error={errors.region} colors={colors} />
      </View>
    </Animated.View>
  );
}

// ============================================================================
// Step 1: Financial Info
// ============================================================================

interface StepFinancialInfoProps {
  formData: Partial<ValuationInput>;
  updateField: <K extends keyof ValuationInput>(key: K, value: ValuationInput[K]) => void;
  errors: Record<string, string>;
  prefilled: ReturnType<typeof prefillFromProfile>;
  colors: ReturnType<typeof useThemeColors>;
}

function StepFinancialInfo({
  formData,
  updateField,
  errors,
  prefilled,
  colors,
}: StepFinancialInfoProps) {
  return (
    <Animated.View entering={FadeInUp.delay(200).duration(400)} style={{ gap: 20, marginTop: 16 }}>
      {/* Has Revenue Toggle */}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingVertical: 8,
        }}
      >
        <View style={{ flex: 1 }}>
          <Text
            className="text-sm font-medium"
            style={{ color: colors.text.primary }}
          >
            Do you have revenue?
          </Text>
          <Text
            className="text-xs"
            style={{
              color: colors.text.tertiary,
              marginTop: 2,
            }}
          >
            Pre-revenue startups use different valuation methods
          </Text>
        </View>
        <Switch
          value={formData.hasRevenue ?? false}
          onValueChange={(v) => updateField('hasRevenue', v)}
          trackColor={{ false: colors.light[300], true: colors.primary[300] }}
          thumbColor={formData.hasRevenue ? colors.primary[600] : colors.light[500]}
        />
      </View>

      {/* Monthly Revenue (shown when hasRevenue is true) */}
      {formData.hasRevenue && (
        <Animated.View entering={FadeIn.duration(300)}>
          <FieldLabel
            label="Monthly Revenue"
            required
            prefilled={!!prefilled.monthlyRevenue}
            colors={colors}
          />
          <CHFInput
            value={formData.monthlyRevenue ?? 0}
            onChange={(v) => updateField('monthlyRevenue', v)}
            placeholder="e.g., 25'000"
            error={errors.monthlyRevenue}
            colors={colors}
          />
          <FieldError error={errors.monthlyRevenue} colors={colors} />
        </Animated.View>
      )}

      {/* Revenue Growth Rate */}
      {formData.hasRevenue && (
        <Animated.View entering={FadeIn.delay(100).duration(300)}>
          <FieldLabel
            label="Revenue Growth Rate (% monthly)"
            prefilled={!!prefilled.revenueGrowthRate}
            colors={colors}
          />
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              backgroundColor: colors.surface.primary,
              borderWidth: 1,
              borderColor: colors.border.light,
              borderRadius: 14,
              height: 48,
              overflow: 'hidden',
            }}
          >
            <TextInput
              className="text-base"
              style={{
                flex: 1,
                height: '100%',
                paddingHorizontal: 16,
                color: colors.text.primary,
              }}
              placeholder="e.g., 15"
              placeholderTextColor={colors.text.tertiary}
              keyboardType="decimal-pad"
              value={
                formData.revenueGrowthRate
                  ? String(formData.revenueGrowthRate)
                  : ''
              }
              onChangeText={(text) => {
                const num = parseFloat(text.replace(/[^0-9.]/g, ''));
                updateField('revenueGrowthRate', isNaN(num) ? 0 : num);
              }}
            />
            <View
              style={{
                paddingHorizontal: 14,
                height: '100%',
                justifyContent: 'center',
                backgroundColor: colors.surface.tertiary,
                borderLeftWidth: 1,
                borderLeftColor: colors.border.light,
              }}
            >
              <Text
                className="text-sm font-semibold"
                style={{ color: colors.text.secondary }}
              >
                %
              </Text>
            </View>
          </View>
        </Animated.View>
      )}

      {/* Business Model */}
      <View>
        <FieldLabel
          label="Business Model"
          required
          prefilled={!!prefilled.businessModel}
          colors={colors}
        />
        <SegmentedControl
          options={BUSINESS_MODEL_OPTIONS}
          value={formData.businessModel ?? 'saas'}
          onChange={(v) => updateField('businessModel', v as BusinessModel)}
          colors={colors}
        />
      </View>

      {/* Has Prototype Toggle */}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingVertical: 8,
        }}
      >
        <View style={{ flex: 1 }}>
          <Text
            className="text-sm font-medium"
            style={{ color: colors.text.primary }}
          >
            Have a working prototype?
          </Text>
          <Text
            className="text-xs"
            style={{
              color: colors.text.tertiary,
              marginTop: 2,
            }}
          >
            MVP, beta, or production product
          </Text>
        </View>
        <Switch
          value={formData.hasPrototype ?? true}
          onValueChange={(v) => updateField('hasPrototype', v)}
          trackColor={{ false: colors.light[300], true: colors.primary[300] }}
          thumbColor={formData.hasPrototype ? colors.primary[600] : colors.light[500]}
        />
      </View>
    </Animated.View>
  );
}

// ============================================================================
// Step 2: Market & Competition
// ============================================================================

interface StepMarketCompetitionProps {
  formData: Partial<ValuationInput>;
  updateField: <K extends keyof ValuationInput>(key: K, value: ValuationInput[K]) => void;
  colors: ReturnType<typeof useThemeColors>;
}

function StepMarketCompetition({
  formData,
  updateField,
  colors,
}: StepMarketCompetitionProps) {
  return (
    <Animated.View entering={FadeInUp.delay(200).duration(400)} style={{ gap: 20, marginTop: 16 }}>
      {/* Market Size */}
      <View>
        <FieldLabel label="Target Addressable Market (TAM)" required colors={colors} />
        <SegmentedControl
          options={MARKET_SIZE_OPTIONS}
          value={formData.marketSize ?? 'medium'}
          onChange={(v) => updateField('marketSize', v as MarketSize)}
          colors={colors}
        />
      </View>

      {/* Competitive Intensity */}
      <View>
        <FieldLabel label="Competitive Intensity" required colors={colors} />
        <SegmentedControl
          options={COMPETITIVE_INTENSITY_OPTIONS}
          value={formData.competitiveIntensity ?? 'medium'}
          onChange={(v) => updateField('competitiveIntensity', v as CompetitiveIntensity)}
          colors={colors}
        />
        <Text
          className="text-xs"
          style={{
            color: colors.text.tertiary,
            marginTop: 6,
          }}
        >
          {formData.competitiveIntensity === 'low'
            ? 'Few direct competitors, large whitespace'
            : formData.competitiveIntensity === 'high'
            ? 'Many established players, crowded market'
            : 'Some competitors, room for differentiation'}
        </Text>
      </View>

      {/* Strategic Partnerships Toggle */}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingVertical: 8,
        }}
      >
        <View style={{ flex: 1 }}>
          <Text
            className="text-sm font-medium"
            style={{ color: colors.text.primary }}
          >
            Strategic partnerships?
          </Text>
          <Text
            className="text-xs"
            style={{
              color: colors.text.tertiary,
              marginTop: 2,
            }}
          >
            Partnerships with corporates, institutions, or key players
          </Text>
        </View>
        <Switch
          value={formData.hasStrategicPartnerships ?? false}
          onValueChange={(v) => updateField('hasStrategicPartnerships', v)}
          trackColor={{ false: colors.light[300], true: colors.primary[300] }}
          thumbColor={
            formData.hasStrategicPartnerships ? colors.primary[600] : colors.light[500]
          }
        />
      </View>
    </Animated.View>
  );
}

// ============================================================================
// Step 3: Growth Plans (VC Method)
// ============================================================================

interface StepGrowthPlansProps {
  formData: Partial<ValuationInput>;
  updateField: <K extends keyof ValuationInput>(key: K, value: ValuationInput[K]) => void;
  errors: Record<string, string>;
  colors: ReturnType<typeof useThemeColors>;
}

function StepGrowthPlans({
  formData,
  updateField,
  errors,
  colors,
}: StepGrowthPlansProps) {
  const exitTimeline = formData.exitTimeline ?? 7;

  return (
    <Animated.View entering={FadeInUp.delay(200).duration(400)} style={{ gap: 24, marginTop: 16 }}>
      {/* Exit Timeline Slider */}
      <View>
        <FieldLabel label="Expected Exit Timeline" required colors={colors} />
        <View
          style={{
            backgroundColor: colors.surface.primary,
            borderRadius: 16,
            padding: 16,
            borderWidth: 1,
            borderColor: colors.border.light,
          }}
        >
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 16,
            }}
          >
            <Text
              className="text-3xl font-bold"
              style={{ color: colors.primary[600] }}
            >
              {exitTimeline}
            </Text>
            <Text
              className="text-sm font-medium"
              style={{ color: colors.text.secondary }}
            >
              years
            </Text>
          </View>

          {/* Timeline selector buttons */}
          <View style={{ flexDirection: 'row', gap: 6 }}>
            {[3, 4, 5, 6, 7, 8, 9, 10].map((yr) => (
              <Pressable
                key={yr}
                onPress={() => updateField('exitTimeline', yr)}
                style={{
                  flex: 1,
                  paddingVertical: 10,
                  borderRadius: 10,
                  alignItems: 'center',
                  backgroundColor:
                    exitTimeline === yr ? colors.primary[600] : colors.surface.tertiary,
                }}
              >
                <Text
                  className={cn('text-sm', exitTimeline === yr ? 'font-semibold' : '')}
                  style={{
                    color: exitTimeline === yr ? '#FFFFFF' : colors.text.secondary,
                  }}
                >
                  {yr}
                </Text>
              </Pressable>
            ))}
          </View>
        </View>
        <FieldError error={errors.exitTimeline} colors={colors} />
      </View>

      {/* Target Raise */}
      <View>
        <FieldLabel label="Target Raise Amount" colors={colors} />
        <CHFInput
          value={formData.targetRaise ?? 0}
          onChange={(v) => updateField('targetRaise', v)}
          placeholder="e.g., 2'000'000"
          colors={colors}
        />
        <Text
          className="text-xs"
          style={{
            color: colors.text.tertiary,
            marginTop: 4,
          }}
        >
          How much are you looking to raise in this round?
        </Text>
      </View>

      {/* Expected Exit Value */}
      <View>
        <FieldLabel label="Expected Exit Value" colors={colors} />
        <CHFInput
          value={formData.expectedExitValue ?? 0}
          onChange={(v) => updateField('expectedExitValue', v)}
          placeholder="e.g., 50'000'000"
          colors={colors}
        />
        <Text
          className="text-xs"
          style={{
            color: colors.text.tertiary,
            marginTop: 4,
          }}
        >
          Your target company valuation at exit (acquisition / IPO)
        </Text>
      </View>

      {/* Info card */}
      <View
        style={{
          flexDirection: 'row',
          gap: 10,
          padding: 14,
          borderRadius: 14,
          backgroundColor: colors.primary[50],
        }}
      >
        <Info size={18} color={colors.primary[600]} style={{ marginTop: 2 }} />
        <Text
          className="text-sm"
          style={{
            flex: 1,
            color: colors.primary[700],
            lineHeight: 18,
          }}
        >
          These inputs feed the VC Method calculation. Leave blank if unsure —
          other methods will still produce a valuation estimate.
        </Text>
      </View>
    </Animated.View>
  );
}

// ============================================================================
// Step 4: Results
// ============================================================================

interface StepResultsProps {
  result: ValuationResult;
  methodBars: ValuationMethodBar[];
  onRecalculate: () => void;
  onBackToDashboard: () => void;
  colors: ReturnType<typeof useThemeColors>;
}

function StepResults({
  result,
  methodBars,
  onRecalculate,
  onBackToDashboard,
  colors,
}: StepResultsProps) {
  const range = result.suggested_range;

  return (
    <View style={{ paddingTop: 24 }}>
      {/* Success Icon */}
      <Animated.View
        entering={FadeIn.duration(200)}
        style={{ alignItems: 'center', marginBottom: 16 }}
      >
        <View
          style={{
            width: 64,
            height: 64,
            borderRadius: 32,
            backgroundColor: colors.traffic.greenLight,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CheckCircle2 size={32} color={colors.success} />
        </View>
      </Animated.View>

      {/* Title */}
      <Animated.View
        entering={FadeInDown.delay(300).duration(400)}
        style={{ alignItems: 'center', marginBottom: 24 }}
      >
        <Text
          className="text-xl font-bold"
          style={{
            color: colors.text.primary,
            textAlign: 'center',
          }}
        >
          Valuation Estimate Ready
        </Text>
        <Text
          className="text-sm"
          style={{
            color: colors.text.secondary,
            textAlign: 'center',
            marginTop: 6,
          }}
        >
          Based on {result.applicable_methods} valuation methods
        </Text>
      </Animated.View>

      {/* Football Field Chart (inline bars) */}
      <Animated.View entering={FadeInUp.delay(500).duration(500)}>
        <View
          style={{
            backgroundColor: colors.surface.primary,
            borderRadius: 20,
            padding: 20,
            borderWidth: 1,
            borderColor: colors.border.light,
            shadowColor: '#000',
            shadowOpacity: 0.05,
            shadowRadius: 10,
            shadowOffset: { width: 0, height: 4 },
            elevation: 3,
          }}
        >
          <Text
            className="text-xs font-semibold"
            style={{
              color: colors.text.tertiary,
              textTransform: 'uppercase',
              letterSpacing: 1,
              marginBottom: 16,
            }}
          >
            Valuation Methods
          </Text>

          {/* Max value for scaling */}
          {(() => {
            const maxVal = Math.max(...methodBars.map((b) => b.value), 1);

            return (
              <View style={{ gap: 14 }}>
                {methodBars.map((bar, index) => {
                  const widthPct = Math.max((bar.value / maxVal) * 100, 15);
                  return (
                    <Animated.View
                      key={bar.method}
                      entering={FadeInUp.delay(600 + index * 100).duration(400)}
                    >
                      <View
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          marginBottom: 6,
                        }}
                      >
                        <Text
                          className="text-sm font-medium"
                          style={{ color: colors.text.primary }}
                        >
                          {bar.label}
                        </Text>
                        <Text
                          className="text-sm font-semibold"
                          style={{ color: colors.text.primary }}
                        >
                          {formatLargeValue(bar.value)}
                        </Text>
                      </View>
                      <View
                        style={{
                          height: 28,
                          borderRadius: 8,
                          backgroundColor: colors.surface.tertiary,
                          overflow: 'hidden',
                        }}
                      >
                        <View
                          style={{
                            width: `${widthPct}%`,
                            height: '100%',
                            borderRadius: 8,
                            backgroundColor: bar.color,
                            alignItems: 'flex-end',
                            justifyContent: 'center',
                            paddingRight: 8,
                          }}
                        >
                          <Text
                            className="text-xs font-semibold"
                            style={{ color: '#FFFFFF' }}
                          >
                            {bar.confidence}%
                          </Text>
                        </View>
                      </View>
                    </Animated.View>
                  );
                })}
              </View>
            );
          })()}
        </View>
      </Animated.View>

      {/* Suggested Range Card */}
      <Animated.View entering={FadeInUp.delay(800).duration(500)}>
        <View
          style={{
            marginTop: 20,
            borderRadius: 20,
            overflow: 'hidden',
          }}
        >
          <LinearGradient
            colors={[colors.primary[600], colors.primary[800]]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={{
              padding: 24,
              borderRadius: 20,
            }}
          >
            <Text
              className="text-xs font-semibold"
              style={{
                color: 'rgba(255,255,255,0.7)',
                textTransform: 'uppercase',
                letterSpacing: 1.5,
                marginBottom: 8,
              }}
            >
              Suggested Valuation Range
            </Text>
            <Text
              className="text-2xl font-bold"
              style={{
                color: '#FFFFFF',
                letterSpacing: -0.5,
              }}
            >
              {formatLargeValue(range.min)} - {formatLargeValue(range.max)}
            </Text>
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 12,
                marginTop: 12,
                paddingTop: 12,
                borderTopWidth: 1,
                borderTopColor: 'rgba(255,255,255,0.2)',
              }}
            >
              <View style={{ flex: 1 }}>
                <Text
                  className="text-xs font-medium"
                  style={{ color: 'rgba(255,255,255,0.6)' }}
                >
                  Median
                </Text>
                <Text
                  className="text-base font-bold"
                  style={{
                    color: '#FFFFFF',
                    marginTop: 2,
                  }}
                >
                  {formatLargeValue(range.median)}
                </Text>
              </View>
              <View
                style={{
                  width: 1,
                  height: 32,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                }}
              />
              <View style={{ flex: 1 }}>
                <Text
                  className="text-xs font-medium"
                  style={{ color: 'rgba(255,255,255,0.6)' }}
                >
                  IQR Range
                </Text>
                <Text
                  className="text-base font-bold"
                  style={{
                    color: '#FFFFFF',
                    marginTop: 2,
                  }}
                >
                  {formatLargeValue(range.iqr_low)} - {formatLargeValue(range.iqr_high)}
                </Text>
              </View>
            </View>
          </LinearGradient>
        </View>
      </Animated.View>

      {/* Disclaimer */}
      <Animated.View entering={FadeIn.delay(1000).duration(400)}>
        <View
          style={{
            flexDirection: 'row',
            gap: 10,
            marginTop: 20,
            padding: 14,
            borderRadius: 14,
            backgroundColor: colors.amber.light,
          }}
        >
          <Info size={16} color={colors.warning} style={{ marginTop: 2 }} />
          <Text
            className="text-xs"
            style={{
              flex: 1,
              color: colors.text.secondary,
              lineHeight: 17,
            }}
          >
            This estimate is for informational purposes only and does not constitute
            financial advice. Valuations are based on standardized methodologies and
            publicly available benchmarks. Actual valuations may differ based on
            market conditions, investor appetite, and deal-specific factors. Consult
            a qualified financial advisor for binding assessments.
          </Text>
        </View>
      </Animated.View>

      {/* Action Buttons */}
      <Animated.View
        entering={FadeInUp.delay(1200).duration(400)}
        style={{ gap: 12, marginTop: 24, paddingBottom: 32 }}
      >
        <AnimatedPressable
          onPress={onRecalculate}
          style={{
            height: 52,
            borderRadius: 14,
            borderWidth: 1.5,
            borderColor: colors.primary[500],
            backgroundColor: 'transparent',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'row',
            gap: 8,
          }}
        >
          <RefreshCw size={18} color={colors.primary[600]} />
          <Text
            className="text-base font-semibold"
            style={{ color: colors.primary[600] }}
          >
            Recalculate
          </Text>
        </AnimatedPressable>

        <AnimatedPressable
          onPress={onBackToDashboard}
          style={{
            height: 52,
            borderRadius: 14,
            backgroundColor: colors.primary[600],
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'row',
            gap: 8,
          }}
        >
          <Text
            className="text-base font-semibold"
            style={{ color: '#FFFFFF' }}
          >
            Back to Dashboard
          </Text>
        </AnimatedPressable>
      </Animated.View>
    </View>
  );
}
